import { useRef, useState } from "react"

interface IUseSearchSessionParams {
    enabled?: boolean
    onSearchPendingChange?: (isPending: boolean) => void
}

function useSearchSession({ enabled, onSearchPendingChange }: IUseSearchSessionParams) {
    const [hasSearched, setHasSearched] = useState(false)
    // Feeds cacheUniqs, which reloads immediately - typing must not bump it, or every
    // key-stroke fires a request (EMTMP-1300).
    const [searchEpoch, setSearchEpoch] = useState(0)
    const currentInputRef = useRef("")

    function startNewSearchEpoch() {
        setSearchEpoch(epoch => epoch + 1)
    }

    return {
        hasSearched,
        searchEpoch,

        syncInput(input: string) {
            currentInputRef.current = input
        },

        handleTyped() {
            setHasSearched(true)
            onSearchPendingChange?.(true)
        },

        handleEnter(isMenuOpen: boolean) {
            if (enabled && isMenuOpen && !hasSearched) {
                setHasSearched(true)
                startNewSearchEpoch()
                onSearchPendingChange?.(true)
            }
        },

        handleMenuClosed() {
            if (!currentInputRef.current && hasSearched) {
                setHasSearched(false)
                startNewSearchEpoch()
            }
        },

        handleCleared() {
            if (hasSearched) {
                setHasSearched(false)
                startNewSearchEpoch()
            }
            onSearchPendingChange?.(false)
        },

        handleCacheKeysChanged() {
            if (enabled && hasSearched) {
                onSearchPendingChange?.(true)
            }
        },

        shouldSkipLoad: !!enabled && !hasSearched,
        notifySkippedLoad() {
            onSearchPendingChange?.(false)
        },
        notifyLoadStart() {
            onSearchPendingChange?.(true)
        },
        notifyLoadEnd(search: string) {
            if (search === currentInputRef.current) {
                onSearchPendingChange?.(false)
            }
        },
    }
}

export default useSearchSession
